import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormsModule } from '@angular/forms';
import { MenuModule } from 'primeng/menu';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuItem } from 'primeng/api';
import markdownDocumentation from '../../modules/besharp-sessions/documentation/besharp-sessions.md' with { loader: 'text' };
import { prettyPrintJson } from 'pretty-print-json';
import {
	BesharpTableHeaderDefinition,
	BesharpTableSize,
} from '../../modules/besharp-table/component/besharp-table.component';
import { BesharpTableComponent } from '../../modules/besharp-table/component/besharp-table.component';
import { MachineService } from '../../services/machine.service';
import { DeployService } from '../../services/deploy.service';
import { SoftwareUpdateService } from '../../services/software-update.service';
import { MenderService, MenderArtifact } from '../../services/mender.service';
import { isMenderFamily, isTraditionalFamily, resolveDeploymentMethod, familyAicToModel } from '../../utils/family-routing';
import { formatArtifactLabel } from '../../utils/artifact-label.util';
import { fetchAuthSession } from 'aws-amplify/auth';

type FleetMachineData = {
	id: any;
	serialNumber: any;
	model: any;
	family: any;
	softwareVersionNumber: any;
	lastConnected: any;
	positionLat: any;
	positionLon: any;
	customerName: any;
	createdAt: any;
	updatedAt: any;
	packageSoftwareVersion: any;
	hasAlarm?: any;
	simId?: any;
	tags?: any;
	name?: any;
	customerGroup?: any;
	router?: any;
	isConnectable?: any;
	isConnected?: any;
	packageSwVersion?: any;
	familyAic?: any;
	status?: string;
	errors?: string[];
};

@Component({
	selector: 'app-fleet-management',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		CardModule,
		BesharpTableComponent,
		ButtonModule,
		ToastModule,
		TooltipModule,
		DialogModule,
		ConfirmDialogModule,
		InputTextModule,
		SelectModule,
		FormsModule,
		ProgressSpinnerModule,
		MenuModule,
		DatePickerModule,
		CheckboxModule,
	],
	providers: [MessageService, ConfirmationService],
	templateUrl: './fleet-management.component.html',
	styleUrl: './fleet-management.component.scss',
})
export class FleetManagementComponent {
	protected responseBody = {};
	@ViewChild('beSharpTable') beSharpTable!: BesharpTableComponent<any>;

	/**
	 * Formats a JSON value for pretty display in the table.
	 * @param json The JSON value to format
	 * @returns HTML string with formatted JSON
	 */
	protected applyJsonFormatting(json: any): string {
		return prettyPrintJson.toHtml(json);
	}

	title = 'beSharp-angular-boilerplate';
	datasource: FleetMachineData[] = [];
	totalRecords = 0;
	backupDatasource: FleetMachineData[] = [];
	loadingAssigned!: boolean;
	loadingUnassigned!: boolean;

	markdownDocumentation = markdownDocumentation;

	tableDefinition: BesharpTableHeaderDefinition[] = [
		{
			def: 'serialNumber',
			title: 'Serial Number',
			sortable: false,
			columnFilter: true,
			multiValueSeparators: [',', ' '],
			transformer: (cellData: string) => cellData.toUpperCase(),
		},
		{ def: 'customerName', title: 'Customer', sortable: false, columnFilter: false },
		{ def: 'family', title: 'Family', sortable: false, columnFilter: false },
		{
			def: 'model',
			title: 'Model',
			sortable: false,
			columnFilter: true,
			virtual: (row: any) => row.familyAic || row.model || '-',
		},
		{
			def: 'status',
			title: 'Status',
			sortable: false,
			columnFilter: false,
			virtual: (row: any) => {
				if (row.status === 'ERROR' && row.errors?.length > 0) {
					return `${row.status} - ${(row.errors as string[]).join(', ')}`;
				}
				return row.status ?? '-';
			},
			tooltip: (row: any) => {
				if (row.status === 'ERROR' && row.errors?.length > 0) {
					return row.errors.join(',');
				}
			},
		},
		{
			def: 'packageSwVersion',
			title: 'SW Package',
			sortable: false,
			columnFilter: true,
			transformer: (cellData: string) => (cellData ? cellData : '-'),
		},
		{
			def: 'softwareVersionNumber',
			title: 'SW Ver',
			sortable: false,
			columnFilter: true,
			transformer: (cellData: string) => (cellData ? cellData : '-'),
		},
		{ def: 'lastConnected', title: 'Last Connected', sortable: false, columnFilter: false },
	];
	actionDefinition = [
		{
			label: 'Details',
			icon: 'pi pi-info-circle',
			styleClass: 'b',
			callback: (row: any) => this.openDetailsModal(row),
		},
	];

	globalFilterFields = this.tableDefinition.map((row) => row.def);

	eBesharpTableSize = BesharpTableSize;

	datasourceLength = 0;

	selectedMachines: FleetMachineData[] = [];
	isDeploySoftwareEnabled = false;
	maxMachinesLimit: number | null = null;

	showDeployModal = false;
	deploymentNotes = '';
	isDeploying = false;
	isScheduled = false;
	minScheduledDate = new Date();
	maxScheduledDate = new Date(Date.now() + 48 * 60 * 60 * 1000);

	deployForm: FormGroup;
	updateModeOptions = [
		{ label: 'Forced Update', value: 'ota' },
		{ label: 'Not Forced Update', value: 'manual' },
	];
	softwareVersionOptions: { label: string; value: string }[] = [];
	selectedSoftwareMap: Map<string, any> = new Map();
	loadingSoftwareList = false;

	// Mender deploy state
	isMenderDeploy = false;
	menderArtifacts: MenderArtifact[] = [];
	selectedArtifactName: string | null = null;
	loadingArtifacts = false;
	artifactLoadError = false;
	artifactPage = 1;
	artifactTotalCount = 0;
	hasMoreArtifacts = false;
	menderForceInstallation = false;
	private menderDeviceTypeFilter: string | undefined = undefined;
	private menderArtifactNamePrefix: string | undefined = undefined;
	private readonly ARTIFACT_PAGE_SIZE = 20;

	showDetailsModal = false;
	selectedMachineDetails: FleetMachineData | null = null;
	machineDetails: any = null;
	loadingDetails = false;

	mobileMenuItems: MenuItem[] = [];

	currentPage = 0;
	pageSize = 10;
	loading = false;
	filters: { [key: string]: any } = {};

	canDeploy = false;

	private refreshTablesListener: ((event: Event) => void) | null = null;

	initializeMobileMenu() {
		this.mobileMenuItems = [
			...(this.canDeploy
				? [
					{
						label: 'Deploy Software',
						icon: 'pi pi-download',
						disabled: !this.isDeploySoftwareEnabled,
						command: () => this.onDeploySoftware(),
					},
				]
				: []),
		];
	}

	constructor(
		protected router: Router,
		protected http: HttpClient,
		private machineService: MachineService,
		private messageService: MessageService,
		private formBuilder: FormBuilder,
		private deployService: DeployService,
		private confirmationService: ConfirmationService,
		private softwareUpdateService: SoftwareUpdateService,
		private menderService: MenderService
	) {
		this.deployForm = this.formBuilder.group({
			softwareVersionId: [{ value: '', disabled: false }, Validators.required],
			updateMode: [{ value: '', disabled: true }],
			deploymentNotes: [{ value: '', disabled: false }],
			scheduledAt: [{ value: null, disabled: false }],
		});

		this.loadUserGroups();
		this.loadDeployLimit();
		this.listMachines(this.pageSize, 0);
	}

	ngOnInit(): void {
		this.initializeMobileMenu();
		this.setupRefreshListener();
	}

	ngOnDestroy(): void {
		this.removeRefreshListener();
	}

	private setupRefreshListener(): void {
		this.refreshTablesListener = () => {
			this.listMachines(this.pageSize, this.currentPage, this.filters);
		};
		window.addEventListener('refreshTable', this.refreshTablesListener);
	}

	private removeRefreshListener(): void {
		if (this.refreshTablesListener) {
			window.removeEventListener('refreshTable', this.refreshTablesListener);
		}
	}

	onSelectionLimitReached(limit: number) {
		this.messageService.add({
			severity: 'warn',
			summary: 'Selection limit reached',
			detail: `You can select at most ${limit} machine(s) for deployment.`,
		});
	}

	rowSelectionEvent(rowData: any) {
		this.selectedMachines = rowData;
		this.updateDeploySoftwareButtonState();
	}

	getSelectedModelsDisplay(): string {
		const unique = [...new Set(
			this.selectedMachines.map(m => m.familyAic || m.model || '-')
		)];
		return unique.join(', ');
	}

	get deployDisabledTooltip(): string {
		if (this.selectedMachines.length === 0) {
			return 'Select one or more machines to deploy';
		}
		const families = [...new Set(this.selectedMachines.map((m) => m.family))];
		const hasMender = families.some((f) => isMenderFamily(f));
		const hasTraditional = families.some((f) => isTraditionalFamily(f));
		if (hasMender && hasTraditional) {
			return 'Cannot deploy to a mix of Fully Automatic (Mender) and Traditional machines — select only one type';
		}
		return 'Selected machines have an unsupported family type';
	}

	updateDeploySoftwareButtonState() {
		if (this.selectedMachines.length === 0 || !this.canDeploy) {
			this.isDeploySoftwareEnabled = false;
			return;
		}
		const families = [...new Set(this.selectedMachines.map((m) => m.family))];

		const hasMender = families.some((f) => isMenderFamily(f));
		const hasTraditional = families.some((f) => isTraditionalFamily(f));
		if (hasMender && hasTraditional) {
			this.isDeploySoftwareEnabled = false;
			this.initializeMobileMenu();
			return;
		}

		const hasKnown = families.some((f) => isMenderFamily(f) || isTraditionalFamily(f));
		if (!hasKnown) {
			this.isDeploySoftwareEnabled = false;
			this.initializeMobileMenu();
			return;
		}

		if (hasMender) {
			const models = [...new Set(this.selectedMachines.map((m) => m.familyAic || m.model || ''))];
			if (models.length > 1) {
				this.isDeploySoftwareEnabled = false;
				this.initializeMobileMenu();
				return;
			}
		}

		this.isDeploySoftwareEnabled = true;
		this.initializeMobileMenu();
	}

	private loadDeployLimit(): void {
		this.deployService.getDeployLimit().subscribe({
			next: (response) => {
				this.maxMachinesLimit = response.limit;
			},
			error: () => {
				this.maxMachinesLimit = null;
			},
		});
	}

	private async loadUserGroups(): Promise<void> {
		try {
			const session = await fetchAuthSession();
			const idToken = session.tokens?.idToken;
			if (idToken) {
				const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
				const groups: string[] = payload['cognito:groups'] || [];
				const isReadOnly = groups.some((group) => group.includes('sw-manager') || group.includes('global-admin'));
				this.canDeploy = !isReadOnly;
			}
		} catch (error) {
			console.error('Error loading user groups:', error);
			this.canDeploy = false;
		}
	}

	onDeploySoftware() {
		if (this.isDeploySoftwareEnabled && this.selectedMachines.length > 0) {
			this.showDeployModal = true;
			this.deployForm.reset();
			this.isScheduled = false;
			this.minScheduledDate = new Date();
			this.maxScheduledDate = new Date(Date.now() + 48 * 60 * 60 * 1000);
			this.deployForm.get('deploymentNotes')?.enable();

			const selectedFamily = this.selectedMachines[0].family;

			if (isMenderFamily(selectedFamily)) {
				this.isMenderDeploy = true;
				const hasSupera = this.selectedMachines.some((m) => m.family?.toLowerCase() === 'supera');
				const hasSseries = this.selectedMachines.some((m) => m.family?.toLowerCase() === 's-series');
				if (hasSupera) {
					this.menderDeviceTypeFilter = 'seco-sys-d18';
					this.menderArtifactNamePrefix = undefined;
				} else if (hasSseries) {
					this.menderDeviceTypeFilter = 'seco-sseries';
					const model = this.selectedMachines[0].familyAic || this.selectedMachines[0].model || '';
					this.menderArtifactNamePrefix = model || undefined;
				} else {
					this.menderDeviceTypeFilter = undefined;
					this.menderArtifactNamePrefix = undefined;
				}
				this.loadMenderArtifacts();
			} else {
				// Traditional family: existing software version loading flow
				this.isMenderDeploy = false;
				this.loadingSoftwareList = true;
				this.softwareVersionOptions = [];
				this.deployForm.get('softwareVersionId')?.enable();

				// Collect unique display models across all selected machines, skip Unsupported
				const allModels = [...new Set(
					this.selectedMachines
						.map(m => familyAicToModel(m.familyAic || m.model || ''))
						.filter(m => m && m.toLowerCase() !== 'unsupported')
				)];

				// Pass all models as comma-separated — backend requires software compatible with ALL
				const filters = allModels.length > 0 ? { models: allModels.join(',') } : {};
				this.softwareUpdateService.getSoftwares(1000, 0, 'version_number', 'asc', filters).subscribe({
					next: (softwareList) => {
						this.loadingSoftwareList = false;
						const filteredSoftware = softwareList.data;
						this.softwareVersionOptions = filteredSoftware.map((software: any) => ({
							label: `${software.versionNumber}`,
							value: software.id,
						}));
						this.selectedSoftwareMap.clear();
						filteredSoftware.forEach((software: any) => {
							this.selectedSoftwareMap.set(software.id, software);
						});
						this.deployForm.get('softwareVersionId')?.valueChanges.subscribe((softwareId) => {
							const selectedSoftware = this.selectedSoftwareMap.get(softwareId);
							if (selectedSoftware && selectedSoftware.forceUpdate !== undefined) {
								const updateModeLabel = selectedSoftware.forceUpdate ? 'Yes' : 'No';
								this.deployForm.patchValue(
									{
										updateMode: updateModeLabel,
									},
									{ emitEvent: false }
								);
							}
						});
					},
					error: (error) => {
						this.loadingSoftwareList = false;
						this.softwareVersionOptions = [];
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: `Failed to load software versions: ${error.message}`,
						});
					},
				});
			}
		}
	}

	loadMenderArtifacts(page: number = 1) {
		if (page === 1) {
			this.menderArtifacts = [];
			this.hasMoreArtifacts = false;
		}
		this.loadingArtifacts = true;
		this.artifactLoadError = false;
		this.artifactPage = page;
		this.menderService.listArtifacts(page, this.ARTIFACT_PAGE_SIZE, this.menderDeviceTypeFilter, this.menderArtifactNamePrefix).subscribe({
			next: (response) => {
				this.menderArtifacts = [...this.menderArtifacts, ...response.artifacts];
				this.artifactTotalCount = response.totalCount;
				this.hasMoreArtifacts = this.menderArtifacts.length < response.totalCount;
				this.loadingArtifacts = false;
			},
			error: () => {
				this.loadingArtifacts = false;
				this.artifactLoadError = true;
				if (page === 1) this.menderArtifacts = [];
			},
		});
	}

	onArtifactScrollEnd() {
		if (!this.loadingArtifacts && this.hasMoreArtifacts) {
			this.loadMenderArtifacts(this.artifactPage + 1);
		}
	}

	retryLoadArtifacts() {
		this.artifactLoadError = false;
		this.menderArtifacts = [];
		this.artifactPage = 1;
		this.loadMenderArtifacts();
	}

	getArtifactOptions(): { label: string; value: string }[] {
		return this.menderArtifacts.map((artifact) => ({
			label: formatArtifactLabel(artifact),
			value: artifact.name,
		}));
	}

	onConfirmDeploy() {
		if (this.isMenderDeploy) {
			this.onConfirmMenderDeploy();
			return;
		}

		if (this.deployForm.valid && this.selectedMachines.length > 0) {
			const formValue = this.deployForm.value;
			const softwareVersionId = formValue.softwareVersionId;
			const scheduledAt: Date | null = this.isScheduled ? formValue.scheduledAt : null;

			if (this.isScheduled && !scheduledAt) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Scheduled Date Required',
					detail: 'Please select a date and time for the scheduled deployment.',
				});
				return;
			}

			const selectedSoftware = this.selectedSoftwareMap.get(softwareVersionId);
			const softwareDisplayName = selectedSoftware
				? `${selectedSoftware.versionNumber} - ${selectedSoftware.description || 'No description'}`
				: softwareVersionId;

			const scheduledLabel = scheduledAt ? ` scheduled for ${scheduledAt.toLocaleString()}` : '';

			this.confirmationService.confirm({
				message: `OTA portal will update ${this.selectedMachines.length} machine(s) with ${softwareDisplayName}${scheduledLabel}, are you sure?`,
				header: 'Confirm Deployment',
				icon: 'pi pi-exclamation-triangle',
				acceptButtonStyleClass: 'p-button-success',
				rejectButtonStyleClass: 'p-button-text',
				accept: () => {
					this.performDeploy();
				},
			});
		}
	}

	private onConfirmMenderDeploy() {
		if (!this.selectedArtifactName) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Artifact Required',
				detail: 'Please select a Mender artifact before deploying.',
			});
			return;
		}

		if (this.selectedMachines.length === 0) {
			return;
		}

		const scheduledAt: Date | null = this.isScheduled ? this.deployForm.value.scheduledAt : null;

		if (this.isScheduled && !scheduledAt) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Scheduled Date Required',
				detail: 'Please select a date and time for the scheduled deployment.',
			});
			return;
		}

		const scheduledLabel = scheduledAt ? ` scheduled for ${scheduledAt.toLocaleString()}` : '';

		this.confirmationService.confirm({
			message: `OTA portal will update ${this.selectedMachines.length} machine(s) with artifact "${this.selectedArtifactName}"${scheduledLabel}, are you sure?`,
			header: 'Confirm Deployment',
			icon: 'pi pi-exclamation-triangle',
			acceptButtonStyleClass: 'p-button-success',
			rejectButtonStyleClass: 'p-button-text',
			accept: () => {
				this.performMenderDeploy();
			},
		});
	}

	private performDeploy() {
		this.isDeploying = true;
		this.deployForm.get('softwareVersionId')?.disable();
		this.deployForm.get('deploymentNotes')?.disable();
		this.deployForm.get('scheduledAt')?.disable();
		const formValue = this.deployForm.value;
		const machines = this.selectedMachines.map((machine) => ({
			id: machine.id,
			serial: machine.serialNumber,
			family: machine.family,
		}));
		const softwareVersionId = formValue.softwareVersionId;
		const updateMode = formValue.updateMode;
		const deploymentNotes = formValue.deploymentNotes;
		const scheduledAt: Date | null = this.isScheduled ? formValue.scheduledAt : null;
		const scheduledAtIso = scheduledAt ? scheduledAt.toISOString() : undefined;

		this.deployService
			.uploadDeployRequest(
				machines,
				softwareVersionId,
				updateMode,
				deploymentNotes,
				scheduledAtIso
			)
			.subscribe({
				next: (response) => {
					this.isDeploying = false;
					this.showDeployModal = false;
					const detail = scheduledAt
						? `Software deployment scheduled for ${scheduledAt.toLocaleString()} on ${this.selectedMachines.length} machine(s).`
						: `Software deployment initiated for ${this.selectedMachines.length} machine(s) with model ${this.selectedMachines[0].model}`;
					this.messageService.add({
						severity: 'success',
						summary: scheduledAt ? 'Deploy Scheduled' : 'Deploy Started',
						detail,
					});
					this.deployForm.reset();
					this.isScheduled = false;
					this.deployForm.get('softwareVersionId')?.disable();
					this.deployForm.get('deploymentNotes')?.disable();
					this.deployForm.get('scheduledAt')?.disable();
				},
				error: (error) => {
					this.isDeploying = false;
					this.deployForm.get('softwareVersionId')?.enable();
					this.deployForm.get('deploymentNotes')?.enable();
					this.deployForm.get('scheduledAt')?.enable();
					this.messageService.add({
						severity: 'error',
						summary: 'Deploy Failed',
						detail: `Failed to deploy software: ${error?.error || error.message || error}`,
					});
				},
			});
	}

	private performMenderDeploy() {
		this.isDeploying = true;

		const machines = this.selectedMachines.map((machine) => ({
			id: machine.id,
			serial: machine.serialNumber,
			family: machine.family,
		}));
		const updateMode = this.deployForm.value.updateMode;
		const deploymentNotes = this.deployForm.value.deploymentNotes;
		const scheduledAt: Date | null = this.isScheduled ? this.deployForm.value.scheduledAt : null;
		const scheduledAtIso = scheduledAt ? scheduledAt.toISOString() : undefined;

		this.deployService
			.uploadDeployRequest(
				machines,
				'',
				updateMode,
				deploymentNotes,
				scheduledAtIso,
				this.selectedArtifactName!,
				this.menderForceInstallation,
			)
			.subscribe({
				next: () => {
					this.isDeploying = false;
					this.showDeployModal = false;
					const detail = scheduledAt
						? `Software deployment scheduled for ${scheduledAt.toLocaleString()} on ${this.selectedMachines.length} machine(s).`
						: `Deployment initiated for ${this.selectedMachines.length} machine(s) with artifact "${this.selectedArtifactName}".`;
					this.messageService.add({
						severity: 'success',
						summary: scheduledAt ? 'Deploy Scheduled' : 'Deploy Started',
						detail,
					});
					this.resetMenderDeployState();
				},
				error: (error) => {
					this.isDeploying = false;
					this.messageService.add({
						severity: 'error',
						summary: 'Deploy Failed',
						detail: `Failed to deploy software: ${error?.error?.message || error?.error || error.message || error}`,
					});
				},
			});
	}

	private resetMenderDeployState() {
		this.deployForm.reset();
		this.isScheduled = false;
		this.isMenderDeploy = false;
		this.menderArtifacts = [];
		this.selectedArtifactName = null;
		this.loadingArtifacts = false;
		this.artifactLoadError = false;
		this.artifactPage = 1;
		this.artifactTotalCount = 0;
		this.hasMoreArtifacts = false;
		this.menderForceInstallation = false;
		this.menderDeviceTypeFilter = undefined;
		this.menderArtifactNamePrefix = undefined;
	}

	onCancelDeploy() {
		this.showDeployModal = false;
		this.deployForm.reset();
		this.isDeploying = false;
		this.isScheduled = false;
		this.loadingSoftwareList = false;
		this.resetMenderDeployState();
	}

	isDeployFormValid(): boolean {
		if (this.isMenderDeploy) {
			return !!this.selectedArtifactName && !this.loadingArtifacts;
		}
		return this.deployForm.valid;
	}

	listMachines(pageSize: number = this.pageSize, offset: number = 0, filters?: { [key: string]: any }) {
		this.datasource = [];
		this.loadingAssigned = true;
		this.machineService.getMachines(offset, pageSize, filters).subscribe({
			next: (machines: any) => {
				this.datasource = machines.data;
				this.datasourceLength = machines.total;
				this.currentPage = offset;
				this.pageSize = pageSize;
				this.loadingAssigned = false;
			},
			error: (error: any) => {
				this.loadingAssigned = false;
				this.datasource = [];
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: `Failed to load machines: ${error.message}`,
				});
			},
		});
	}

	onPageChange(event: any): void {
		this.currentPage = event.first;
		this.pageSize = event.rows;
		this.listMachines(this.pageSize, this.currentPage, this.filters);
	}

	clearFilters() {
		this.filters = {};
		this.selectedMachines = [];
		this.isDeploySoftwareEnabled = false;
		this.selectedMachineDetails = null;
		this.currentPage = 0;
		this.beSharpTable?.resetTable();
		this.listMachines(this.pageSize, 0);
	}

	onColumnFilterChange(event: any) {
    	this.filters = {
			...event.filters,
			serialNumber: undefined
		};

		if (event.filters['serialNumber']?.length > 0) {
			const rawValues = event.filters['serialNumber']
				.map((f: any) => f.value)
				.filter((v: any) => v != null && v !== '');
			const allValues = rawValues.flatMap((v: any) => Array.isArray(v) ? v : [v]).filter((v: string) => v.length > 0);
			if (allValues.length > 0) {
				this.filters['serialNumbers'] = allValues.join(',');
			}
		}

		this.listMachines(this.pageSize, 0, this.filters);
	}

	openDetailsModal(machine: FleetMachineData) {
		this.selectedMachineDetails = machine;
		this.machineDetails = null;
		this.loadingDetails = true;
		this.showDetailsModal = true;
		this.machineService.getMachineDetails(machine.serialNumber).subscribe({
			next: (details: any) => {
				this.machineDetails = details;
				this.loadingDetails = false;
			},
			error: (error: any) => {
				console.error('Failed to load machine details:', error);
				this.loadingDetails = false;
			},
		});
	}

	closeDetailsModal() {
		this.showDetailsModal = false;
		this.selectedMachineDetails = null;
		this.machineDetails = null;
		this.loadingDetails = false;
	}

	refreshMachineList(): void {
		this.listMachines(this.pageSize, this.currentPage, this.filters);
	}
}
